import * as React from "react";
import {useState, type JSX} from "react";
import {KeyString, OrdererFunction, OrderField, sortRecords} from "@/tools";
import {Table, TableBody, TableCell, TableContainer, TableFooter, TablePagination, TableRow} from "@mui/material";
import {HeaderWithOrdering, FormattedAlert, HeaderFieldDescriptor} from "@dvrt/graphql-widgets";
import {defineMessage, MessageDescriptor} from "react-intl";

const loadingStyle: React.CSSProperties = {
    pointerEvents: "none",
    opacity: 0.4,
};

const getFirstKey = <T extends Record<string, unknown>>(obj: T | undefined): KeyString<T> | undefined => {
    if (obj === undefined) return undefined;
    return Object.keys(obj)[0] as KeyString<T>;
};

type Props<T extends Record<string, unknown>> = {
    records: T[];
    loading: boolean;
    fields: {name: keyof T; label: MessageDescriptor; sortable?: boolean}[];
    generators: {[K in keyof T]?: (record: T) => any};
    initialOrder?: OrderField<T>[]; // allowing only fields of T for now
    // the actions is a misnomer, as it is actually used for extra fields (eg voicemail info)
    actions?: Action<T>[];
    style?: React.CSSProperties;
};

// TODO: better documentation
/**
 * Type for actions that can be performed on a record.
 * Actions are appended to the right side of the table.
 * The RecordList can handle multiple Actions, but it is
 * recommended to use only one, as it can look weird if there
 * are too many actions. (Especially if only a few of them have
 * labels)
 *
 * @param renderer A function that gets the corresponding record, and gives back the JSX to render.
 * @param header Optional header for the column of the action.
 * @param orderer Orderer function that handles the ordering of the column. (required
 * if header is set)
 **/
type Action<T> = {
    renderer: (record: T) => JSX.Element | React.ReactNode;
} & (
    | {
          // Do not allow setting 'orderer' if 'header' is not set
          header: Header;
          orderer?: OrdererFunction<T>;
      }
    | {
          header?: never;
          orderer?: never;
      }
);

/**
 * Type for the Action header.
 * @param label The translation id for the label. The label will be in the header row.
 **/
type Header = {
    name: string;
    label: MessageDescriptor; // translation id
};

/**
 * List of records with pagination and sorting.
 *
 * @param records the records to display
 * @param loading whether the calls are still loading
 * @param fields the fields to display of the records
 * @param generators the generators that display the content of the fields
 * @param initialOrder the initial order of the fields
 * @param actions the actions for the corresponding records
 * @param style the style to apply to the table
 */
export const RecordList = <T extends Record<string, unknown>>({
    records,
    loading,
    style,
    initialOrder,
    fields,
    generators,
    actions = [],
}: Props<T>) => {
    // Indicating it can not be anything else than what is in actions' labels
    type ActionLabel = (typeof actions)[number]["header"]["label"];

    // A label can be the 'ordering variant' (~) of type T and the action labels
    type Label = OrderField<T> | OrderField<ActionLabel>;

    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(20);
    const [order, setOrder] = useState<Label[]>(initialOrder || []);

    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getHeaders = (): HeaderFieldDescriptor[] => {
        return fields
            .map((field) => ({path: field.name as string, label: field.label}))
            .concat(
                actions.map((action) =>
                    action.header ? {path: action.header.name, label: action.header.label} : null,
                ),
            );
    };

    const getRows = () => {
        return records
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((record, idx) => (
                <TableRow key={idx}>
                    {fields
                        .map((field) => (
                            <TableCell key={field.name as string}>
                                {generators[field.name] && generators[field.name](record)}
                            </TableCell>
                        ))
                        .concat(
                            actions.map((action, index) => (
                                <TableCell key={action.header?.label?.id ?? index}>{action.renderer(record)}</TableCell>
                            )),
                        )}
                </TableRow>
            ));
    };

    const orderableColumns: string[] = fields
        .filter((field) => !(field.sortable === false)) // By default fields are sortable, unless specified otherwise
        .map((field) => field.name as string)
        .concat(
            actions
                .map((action) => (action.orderer ? action.header?.name : undefined))
                .filter((label) => label !== undefined),
        );

    // Getting the orderers for columns that may need to be ordered in a custom way
    const customOrderers: Record<string, OrdererFunction<T>> = actions.reduce(
        (acc, action) => {
            if (action.header?.name && action.orderer) {
                acc[action.header.name] = action.orderer;
            }
            return acc;
        },
        {} as Record<string, OrdererFunction<T>>,
    );

    records = sortRecords<T, ActionLabel>(
        records,
        order,
        records.length ? getFirstKey(records[0]) : undefined,
        customOrderers,
    );

    return (
        <TableContainer style={style}>
            <Table size="small" style={{...(loading ? loadingStyle : {})}}>
                <HeaderWithOrdering
                    fields={getHeaders()}
                    multipleOrdering={false}
                    order={order}
                    orderableFields={orderableColumns}
                    setOrder={(o) => setOrder(o as (OrderField<T> | OrderField<ActionLabel>)[])} // It is ensured that the order is of the correct type
                />
                <TableBody>
                    {getRows()}
                    {records.length === 0 && (
                        <tr>
                            <td colSpan={100}>
                                <FormattedAlert
                                    message={defineMessage({
                                        id: "call_list.empty",
                                        defaultMessage: "Could not find any records matching the criteria",
                                    })}
                                    severity="info"
                                    containerStyle={{margin: 15}}
                                />
                            </td>
                        </tr>
                    )}
                </TableBody>
                <TableFooter>
                    <tr>
                        <td colSpan={100}>
                            <TablePagination
                                component="div"
                                rowsPerPageOptions={[20, 50, 100]}
                                count={records.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </td>
                    </tr>
                </TableFooter>
            </Table>
        </TableContainer>
    );
};
